import React, { useState, useEffect, useCallback } from 'react';
import {
  FaPlus, FaTrash, FaEdit, FaCalendarAlt, FaMapMarkerAlt,
  FaClock, FaUsers, FaTags, FaUserTie,
  FaCertificate, FaBook, FaFire, FaStar, FaTimes, FaCheck,
  FaGraduationCap, FaLayerGroup,
  FaToggleOn, FaToggleOff, FaSearch, FaExclamationTriangle,
  FaRegClock,
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../context/ThemeContext';
import { API_URL, resolveImageUrl } from '../config/api';

const SB_OPEN = 252;
const SB_CLOSED = 74;
const SB_GAP = 12;
const NB_HEIGHT = 68;
const NB_TOP = 12;

//  Skeleton 
const Sk = ({ h = 60, r = 12, mb = 0 }) => (
  <div style={{
    height: h, borderRadius: r, marginBottom: mb,
    background: 'linear-gradient(90deg,rgba(128,128,128,0.08) 25%,rgba(128,128,128,0.15) 50%,rgba(128,128,128,0.08) 75%)',
    backgroundSize: '200% 100%', animation: 'fm-shimmer 1.4s infinite',
  }} />
);

//  Toast notification 
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors = {
    success: { bg: '#198754', border: 'rgba(25,135,84,0.4)' },
    error: { bg: '#dc3545', border: 'rgba(220,53,69,0.4)' },
    warning: { bg: '#fd7e14', border: 'rgba(253,126,20,0.4)' },
  };
  const col = colors[type] || colors.success;

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      display: 'flex', alignItems: 'center', gap: 12,
      background: '#0d1f14', border: `1px solid ${col.border}`,
      borderLeft: `4px solid ${col.bg}`,
      borderRadius: 14, padding: '14px 20px',
      boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
      animation: 'fm-slideIn 0.4s cubic-bezier(0.34,1.56,0.64,1)',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      maxWidth: 380,
    }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: col.bg, flexShrink: 0 }} />
      <span style={{ fontSize: 14, fontWeight: 600, color: '#eef2f7' }}>{message}</span>
      <button onClick={onClose} style={{
        background: 'none', border: 'none', color: 'rgba(200,212,224,0.5)',
        cursor: 'pointer', fontSize: 12, marginLeft: 'auto', padding: 0,
      }}>✕</button>
    </div>
  );
};

//  Modal de confirmation suppression 
const ConfirmModal = ({ item, onConfirm, onCancel, isDark, c }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    animation: 'fm-fadeIn 0.2s ease',
  }}>
    <div style={{
      background: isDark ? '#0d1a27' : '#ffffff',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
      borderRadius: 20, padding: 40, maxWidth: 420, width: '90%',
      boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
      animation: 'fm-scaleIn 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: 16, margin: '0 auto 20px',
        background: 'rgba(220,53,69,0.1)', border: '1px solid rgba(220,53,69,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 28, color: '#dc3545',
      }}>
        <FaExclamationTriangle />
      </div>
      <h3 style={{ fontSize: 20, fontWeight: 800, color: c.title, textAlign: 'center', marginBottom: 12 }}>
        Supprimer cette formation ?
      </h3>
      <p style={{ fontSize: 14, color: c.textDim, textAlign: 'center', marginBottom: 32, lineHeight: 1.6 }}>
        <strong style={{ color: c.text }}>{item?.title}</strong> sera définitivement supprimée.
        Cette action est irréversible.
      </p>
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={onCancel} style={{
          flex: 1, padding: '12px 20px', borderRadius: 10,
          background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#e2e8f0'}`,
          color: c.text, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>Annuler</button>
        <button onClick={onConfirm} style={{
          flex: 1, padding: '12px 20px', borderRadius: 10,
          background: 'linear-gradient(135deg, #dc3545, #c82333)',
          border: 'none', color: 'white', fontSize: 14, fontWeight: 700,
          cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
          boxShadow: '0 4px 16px rgba(220,53,69,0.35)',
        }}>Supprimer</button>
      </div>
    </div>
  </div>
);

//  Champ formulaire 
const Field = ({ label, required, children, isDark, c }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <label style={{ fontSize: 13, fontWeight: 700, color: c.textDim }}>
      {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
    </label>
    {children}
  </div>
);

//  Styles communs pour les inputs 
const inputStyle = (isDark, c) => ({
  padding: '11px 14px', borderRadius: 10, fontSize: 14,
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
  border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'}`,
  color: c.text, outline: 'none', width: '100%', boxSizing: 'border-box',
  transition: 'border-color 0.2s',
});

//  Formulaire de création 
const FormationForm = ({ onClose, onSave, editData, isDark, c }) => {
  const isEdit = !!editData;

  const [formType, setFormType] = useState(editData?.form_type || 'session');
  const [loading, setLoading] = useState(false);

  // Champs communs
  const [title, setTitle] = useState(editData?.title || '');
  const [domain, setDomain] = useState(editData?.domain || '');
  const [price, setPrice] = useState(editData?.price || '');
  const [description, setDescription] = useState(editData?.description || '');
  const [imageUrl, setImageUrl] = useState('');  // URL externe (vide par défaut, même en édition)
  const [imageFile, setImageFile] = useState(null);  // Fichier upload
  const [imagePreview, setImagePreview] = useState(resolveImageUrl(editData?.image) || '');  // Aperçu avec URL résolue
  const [mode, setMode] = useState(editData?.mode || 'Présentiel');
  const [isActive, setIsActive] = useState(editData?.is_active !== false);

  // Gestion upload fichier image
  const handleImageFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImageUrl('');
    setImagePreview(URL.createObjectURL(file));
  };

  // Quand on tape une URL, on efface le fichier
  const handleImageUrl = (val) => {
    setImageUrl(val);
    setImageFile(null);
    setImagePreview(val);
  };

  // Champs SESSION
  const [instructor, setInstructor] = useState(editData?.instructor || '');
  const [startDate, setStartDate] = useState(editData?.startDate || '');
  const [endDate, setEndDate] = useState(editData?.endDate || '');
  const [duration, setDuration] = useState(editData?.duration || '');
  const [city, setCity] = useState(editData?.city || '');
  const [seats, setSeats] = useState(editData?.seats || '');
  const [seatsAvailable, setSeatsAvailable] = useState(editData?.seatsAvailable || '');
  const [level, setLevel] = useState(editData?.level || 'Intermédiaire');
  const [isPopular, setIsPopular] = useState(editData?.isPopular || false);
  const [isNewSession, setIsNewSession] = useState(editData?.isNewSession || false);
  const [rating, setRating] = useState(editData?.rating || '4.5');

  // Champs CERTIFICATION
  const [code, setCode] = useState(editData?.code || '');
  const [certDuration, setCertDuration] = useState(editData?.certDuration || '');
  const [niveau, setNiveau] = useState(editData?.niveau || '');
  const [prerequis, setPrerequis] = useState(editData?.prerequis || '');
  const [experience, setExperience] = useState(editData?.experience || '');
  const [examen, setExamen] = useState(editData?.examen || '');
  const [modules, setModules] = useState((editData?.modules || ['']).join('\n'));
  const [competences, setCompetences] = useState((editData?.competences || ['']).join('\n'));
  const [debouches, setDebouches] = useState((editData?.debouches || ['']).join('\n'));
  const [color, setColor] = useState(editData?.color || '#198754');

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setLoading(true);

    // On utilise FormData pour supporter l'upload de fichier image
    const formData = new FormData();
    formData.append('form_type', formType);
    formData.append('title', title);
    formData.append('domain', domain);
    formData.append('price', price);
    formData.append('description', description);
    formData.append('mode', mode);
    formData.append('is_active', isActive ? '1' : '0');

    // Image : 3 cas possibles
    // 1. Nouveau fichier uploadé
    if (imageFile) {
      formData.append('image', imageFile);
    } 
    // 2. Nouvelle URL externe valide tapée
    else if (imageUrl && imageUrl.trim() && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
      formData.append('image_url', imageUrl.trim());
    }
    // 3. Pas de changement : ne rien envoyer, le backend garde l'image existante

    if (formType === 'session') {
      formData.append('start_date', startDate);
      formData.append('end_date', endDate);
      formData.append('duration', duration);
      formData.append('city', city);
      formData.append('seats', seats || 0);
      formData.append('seats_available', seatsAvailable || 0);
      formData.append('instructor', instructor);
      formData.append('level', level);
      formData.append('rating', rating || 4.5);
      formData.append('is_popular', isPopular ? '1' : '0');
      formData.append('is_new_session', isNewSession ? '1' : '0');
    } else {
      formData.append('code', code);
      formData.append('duration', certDuration);
      formData.append('niveau', niveau);
      formData.append('prerequis', prerequis);
      formData.append('experience', experience);
      formData.append('examen', examen);
      formData.append('color', color);
      // Laravel reçoit les tableaux sous forme modules[0], modules[1]…
      modules.split('\n').filter(Boolean).forEach((m, i) => formData.append(`modules[${i}]`, m));
      competences.split('\n').filter(Boolean).forEach((c, i) => formData.append(`competences[${i}]`, c));
      debouches.split('\n').filter(Boolean).forEach((d, i) => formData.append(`debouches[${i}]`, d));
    }

    // Note: On utilise POST pour la création ET la mise à jour
    // Le backend gère la différence via l'ID dans l'URL

    await onSave(formData, isEdit ? editData.id : null);
    setLoading(false);
  };

  const sectionStyle = {
    background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0'}`,
    borderRadius: 14, padding: '20px 24px', marginBottom: 20,
  };

  const sectionTitle = (icon, text) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <div style={{ color: '#198754', fontSize: 14 }}>{icon}</div>
      <span style={{ fontSize: 13, fontWeight: 800, color: c.title, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{text}</span>
    </div>
  );

  const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 };

  const inp = (value, setter, placeholder, type = 'text') => (
    <input
      type={type} value={value} onChange={e => setter(e.target.value)}
      placeholder={placeholder} style={inputStyle(isDark, c)}
    />
  );

  const sel = (value, setter, options) => (
    <select value={value} onChange={e => setter(e.target.value)} style={inputStyle(isDark, c)}>
      {options.map(o => <option key={o.v || o} value={o.v || o}>{o.l || o}</option>)}
    </select>
  );

  const textarea = (value, setter, placeholder, rows = 4) => (
    <textarea
      value={value} onChange={e => setter(e.target.value)}
      placeholder={placeholder} rows={rows}
      style={{ ...inputStyle(isDark, c), resize: 'vertical', lineHeight: 1.6 }}
    />
  );

  const toggle = (checked, setter, label) => (
    <div
      onClick={() => setter(!checked)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
        padding: '10px 14px', borderRadius: 10,
        background: checked ? 'rgba(25,135,84,0.1)' : isDark ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
        border: `1px solid ${checked ? 'rgba(25,135,84,0.3)' : isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
        transition: 'all 0.2s',
      }}
    >
      <div style={{
        width: 40, height: 22, borderRadius: 11, position: 'relative', flexShrink: 0,
        background: checked ? '#198754' : isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1',
        transition: 'background 0.2s',
      }}>
        <div style={{
          position: 'absolute', top: 3, left: checked ? 21 : 3,
          width: 16, height: 16, borderRadius: '50%',
          background: 'white', transition: 'left 0.2s',
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, color: checked ? '#198754' : c.textDim }}>{label}</span>
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '20px 16px', overflowY: 'auto',
      animation: 'fm-fadeIn 0.2s ease',
    }}>
      <div style={{
        width: '100%', maxWidth: 760, marginTop: 20,
        background: isDark ? '#0a1628' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
        borderRadius: 24, overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
        animation: 'fm-scaleIn 0.35s cubic-bezier(0.34,1.56,0.64,1)',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}>
        {/* Header formulaire */}
        <div style={{
          padding: '24px 32px', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9'}`,
          background: isDark ? 'rgba(25,135,84,0.06)' : 'linear-gradient(135deg, #f0fdf4, #f8fafc)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 46, height: 46, borderRadius: 12,
              background: 'linear-gradient(135deg, #198754, #20c997)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, color: 'white',
              boxShadow: '0 4px 16px rgba(25,135,84,0.35)',
            }}>
              {isEdit ? <FaEdit /> : <FaPlus />}
            </div>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: c.title, margin: 0 }}>
                {isEdit ? 'Modifier la formation' : 'Créer une nouvelle formation'}
              </h2>
              <p style={{ fontSize: 13, color: c.textDim, margin: 0 }}>
                {isEdit ? 'Modifiez les informations ci-dessous' : 'Remplissez les informations de la formation'}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 36, height: 36, borderRadius: 10, border: 'none', cursor: 'pointer',
            background: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
            color: c.textDim, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><FaTimes /></button>
        </div>

        {/* Contenu formulaire */}
        <div style={{ padding: '28px 32px', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>

          {/* Sélecteur de type */}
          {!isEdit && (
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: c.textDim, display: 'block', marginBottom: 10 }}>
                TYPE DE FORMATION <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { v: 'session', icon: <FaCalendarAlt />, label: 'Session en cours', sub: 'Formation classique avec dates et formateur' },
                  { v: 'certification', icon: <FaCertificate />, label: 'Certification CINEF', sub: 'Programme certifiant avec code et modules' },
                ].map(t => (
                  <div
                    key={t.v}
                    onClick={() => setFormType(t.v)}
                    style={{
                      padding: '16px 20px', borderRadius: 14, cursor: 'pointer',
                      background: formType === t.v
                        ? isDark ? 'rgba(25,135,84,0.15)' : '#f0fdf4'
                        : isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                      border: `2px solid ${formType === t.v ? '#198754' : isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <span style={{ color: formType === t.v ? '#198754' : c.textDim, fontSize: 16 }}>{t.icon}</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: formType === t.v ? '#198754' : c.title }}>{t.label}</span>
                    </div>
                    <p style={{ fontSize: 12, color: c.textDim, margin: 0 }}>{t.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section : Informations générales */}
          <div style={sectionStyle}>
            {sectionTitle(<FaTags />, 'Informations générales')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Field label="Titre de la formation" required isDark={isDark} c={c}>
                {inp(title, setTitle, 'Ex: Gestion des Marchés Publics')}
              </Field>
              <div style={grid2}>
                <Field label="Domaine" isDark={isDark} c={c}>
                  {inp(domain, setDomain, 'Ex: Juridique, Finance, RH...')}
                </Field>
                <Field label="Prix (€ ou FCFA)" isDark={isDark} c={c}>
                  {inp(price, setPrice, 'Ex: 1 900 € ou 950 000 FCFA')}
                </Field>
              </div>
              <div style={grid2}>
                <Field label="Mode de formation" isDark={isDark} c={c}>
                  {sel(mode, setMode, ['Présentiel', 'Classe Virtuelle', 'Blended Learning', 'E-Learning', 'E-learning / Présentiel', 'Présentiel │ Classe Virtuelle'])}
                </Field>
                {formType === 'certification' && (
                  <Field label="Code de référence" required isDark={isDark} c={c}>
                    {inp(code, setCode, 'Ex: CC26014, GJ26020...')}
                  </Field>
                )}
                {formType === 'session' && (
                  <Field label="Niveau" isDark={isDark} c={c}>
                    {sel(level, setLevel, ['Débutant', 'Intermédiaire', 'Avancé', 'Expert'])}
                  </Field>
                )}
              </div>
              <Field label="Description" isDark={isDark} c={c}>
                {textarea(description, setDescription, 'Décrivez les objectifs et le contenu de la formation...', 3)}
              </Field>
              <Field label="Image" isDark={isDark} c={c}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Option 1 : URL externe */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: c.textDim, whiteSpace: 'nowrap' }}>URL externe</span>
                    <input
                      type="text" value={imageUrl}
                      onChange={e => handleImageUrl(e.target.value)}
                      placeholder="https://images.unsplash.com/..."
                      style={inputStyle(isDark, c)}
                    />
                  </div>
                  {/* Séparateur */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, height: 1, background: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0' }} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: c.textDim }}>OU</span>
                    <div style={{ flex: 1, height: 1, background: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0' }} />
                  </div>
                  {/* Option 2 : Upload fichier */}
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                    padding: '10px 14px', borderRadius: 10,
                    background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                    border: `2px dashed ${imageFile ? '#198754' : isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'}`,
                    transition: 'all 0.2s',
                  }}>
                    <span style={{ fontSize: 18 }}>📁</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: imageFile ? '#198754' : c.text }}>
                        {imageFile ? imageFile.name : 'Choisir un fichier image'}
                      </div>
                      <div style={{ fontSize: 11, color: c.textDim }}>JPG, PNG, WEBP — max 4 Mo</div>
                    </div>
                    <input type="file" accept="image/jpeg,image/png,image/jpg,image/webp" onChange={handleImageFile} style={{ display: 'none' }} />
                  </label>
                </div>
              </Field>
              {imagePreview && (
                <div style={{ borderRadius: 12, overflow: 'hidden', height: 140, position: 'relative' }}>
                  <img src={imagePreview} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button onClick={() => { setImageFile(null); setImageUrl(''); setImagePreview(''); }} style={{
                    position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: 8,
                    background: 'rgba(220,53,69,0.9)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>✕</button>
                </div>
              )}
            </div>
          </div>

          {/* Section SESSION */}
          {formType === 'session' && (
            <>
              <div style={sectionStyle}>
                {sectionTitle(<FaCalendarAlt />, 'Dates et logistique')}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={grid2}>
                    <Field label="Date de début" required isDark={isDark} c={c}>
                      {inp(startDate, setStartDate, '', 'date')}
                    </Field>
                    <Field label="Date de fin" isDark={isDark} c={c}>
                      {inp(endDate, setEndDate, '', 'date')}
                    </Field>
                  </div>
                  <div style={grid2}>
                    <Field label="Durée" isDark={isDark} c={c}>
                      {inp(duration, setDuration, 'Ex: 5 jours, 2 semaines...')}
                    </Field>
                    <Field label="Ville / Lieu" isDark={isDark} c={c}>
                      {inp(city, setCity, 'Ex: Cotonou, Lomé, En ligne...')}
                    </Field>
                  </div>
                  <div style={grid2}>
                    <Field label="Nombre total de places" isDark={isDark} c={c}>
                      {inp(seats, setSeats, '25', 'number')}
                    </Field>
                    <Field label="Places disponibles" isDark={isDark} c={c}>
                      {inp(seatsAvailable, setSeatsAvailable, '25', 'number')}
                    </Field>
                  </div>
                </div>
              </div>

              <div style={sectionStyle}>
                {sectionTitle(<FaUserTie />, 'Formateur et paramètres')}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={grid2}>
                    <Field label="Nom du formateur" isDark={isDark} c={c}>
                      {inp(instructor, setInstructor, 'Ex: Dr. Kouassi André')}
                    </Field>
                    <Field label="Note (sur 5)" isDark={isDark} c={c}>
                      {inp(rating, setRating, '4.8', 'number')}
                    </Field>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    {toggle(isPopular, setIsPopular, '⭐ Formation populaire')}
                    {toggle(isNewSession, setIsNewSession, '🔥 Nouvelle session')}
                    {toggle(isActive, setIsActive, '✅ Session active')}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Section CERTIFICATION */}
          {formType === 'certification' && (
            <>
              <div style={sectionStyle}>
                {sectionTitle(<FaGraduationCap />, 'Paramètres de certification')}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={grid2}>
                    <Field label="Durée de la formation" isDark={isDark} c={c}>
                      {inp(certDuration, setCertDuration, 'Ex: 3 jours, 5 jours...')}
                    </Field>
                    <Field label="Niveau" isDark={isDark} c={c}>
                      {inp(niveau, setNiveau, 'Ex: Fondation (Débutant/Administratif)')}
                    </Field>
                  </div>
                  <div style={grid2}>
                    <Field label="Prérequis" isDark={isDark} c={c}>
                      {inp(prerequis, setPrerequis, 'Ex: BAC+3, Master...')}
                    </Field>
                    <Field label="Expérience requise" isDark={isDark} c={c}>
                      {inp(experience, setExperience, "Ex: Aucune, 5 ans minimum...")}
                    </Field>
                  </div>
                  <div style={grid2}>
                    <Field label="Examen de certification" isDark={isDark} c={c}>
                      {inp(examen, setExamen, 'Ex: 80 questions, 120 questions...')}
                    </Field>
                    <Field label="Couleur accent" isDark={isDark} c={c}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {['#198754', '#20c997', '#fd7e14', '#dc3545', '#6f42c1', '#0d6efd'].map(col => (
                          <div
                            key={col}
                            onClick={() => setColor(col)}
                            style={{
                              width: 28, height: 28, borderRadius: 8, background: col, cursor: 'pointer',
                              border: color === col ? '3px solid white' : '3px solid transparent',
                              boxShadow: color === col ? `0 0 0 2px ${col}` : 'none',
                              transition: 'all 0.2s',
                            }}
                          />
                        ))}
                      </div>
                    </Field>
                  </div>
                </div>
              </div>

              <div style={sectionStyle}>
                {sectionTitle(<FaLayerGroup />, 'Contenu pédagogique')}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Field label="Modules (un par ligne)" isDark={isDark} c={c}>
                    {textarea(modules, setModules, 'Fondations Administratives et Cycle RH\nAcquisition et Administration de la Paie\n...', 5)}
                  </Field>
                  <Field label="Compétences acquises (une par ligne)" isDark={isDark} c={c}>
                    {textarea(competences, setCompetences, 'Exécution des processus administratifs RH\nCompréhension du cycle de vie de l\'employé\n...', 4)}
                  </Field>
                  <Field label="Débouchés métiers (un par ligne)" isDark={isDark} c={c}>
                    {textarea(debouches, setDebouches, 'Assistant RH\nGestionnaire du personnel\n...', 3)}
                  </Field>
                </div>
              </div>

              <div style={{ ...sectionStyle, marginBottom: 0 }}>
                {sectionTitle(<FaCheck />, 'Visibilité')}
                {toggle(isActive, setIsActive, '✅ Certification active et visible sur le site')}
              </div>
            </>
          )}
        </div>

        {/* Footer formulaire */}
        <div style={{
          padding: '20px 32px', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9'}`,
          display: 'flex', justifyContent: 'flex-end', gap: 12,
          background: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa',
        }}>
          <button onClick={onClose} style={{
            padding: '12px 24px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600,
            background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#e2e8f0'}`,
            color: c.text, fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}>Annuler</button>
          <button
            onClick={handleSubmit}
            disabled={loading || !title.trim()}
            style={{
              padding: '12px 32px', borderRadius: 10, cursor: loading || !title.trim() ? 'not-allowed' : 'pointer',
              fontSize: 14, fontWeight: 700,
              background: loading || !title.trim()
                ? isDark ? 'rgba(25,135,84,0.3)' : '#d1fae5'
                : 'linear-gradient(135deg, #198754, #20c997)',
              border: 'none', color: 'white',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              boxShadow: loading || !title.trim() ? 'none' : '0 4px 16px rgba(25,135,84,0.35)',
              display: 'flex', alignItems: 'center', gap: 8,
              transition: 'all 0.2s',
            }}
          >
            {loading ? (
              <><span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'fm-spin 0.8s linear infinite' }} /> Enregistrement...</>
            ) : (
              <><FaCheck /> {isEdit ? 'Enregistrer les modifications' : 'Créer la formation'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

//  Carte SESSION ─
const SessionCard = ({ session, onEdit, onDelete, onToggle, isDark, c }) => {
  const pct = session.seats ? ((session.seats - session.seatsAvailable) / session.seats) * 100 : 0;
  const occupancyColor = pct >= 80 ? '#dc3545' : pct >= 50 ? '#fd7e14' : '#198754';
  const fmt = d => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div style={{
      background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9'}`,
      borderRadius: 16, overflow: 'hidden', transition: 'all 0.3s ease',
      boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.06)',
      opacity: session.is_active === false ? 0.6 : 1,
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.15)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.06)'; }}
    >
      {/* Image */}
      <div style={{ position: 'relative', height: 160, overflow: 'hidden' }}>
        {session.image ? (
          <img src={resolveImageUrl(session.image)} alt={session.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #198754, #20c997)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FaCalendarAlt style={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
          </div>
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.6))' }} />
        <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {session.isNewSession && <span style={{ background: 'rgba(253,126,20,0.95)', color: 'white', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4 }}><FaFire />Nouveau</span>}
          {session.isPopular && <span style={{ background: 'rgba(255,193,7,0.95)', color: '#1a202c', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4 }}><FaStar />Populaire</span>}
        </div>
        <div style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(255,255,255,0.95)', color: '#198754', fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 6 }}>
          {session.mode || session.type}
        </div>
        {/* Boutons action */}
        <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6 }}>
          <button onClick={() => onToggle(session)} title={session.is_active !== false ? 'Désactiver' : 'Activer'} style={{
            width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer',
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
            color: session.is_active !== false ? '#20c997' : '#9ca3af', fontSize: 13,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{session.is_active !== false ? <FaToggleOn /> : <FaToggleOff />}</button>
        </div>
      </div>

      {/* Contenu */}
      <div style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <FaTags style={{ color: '#198754', fontSize: 10 }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#198754', background: isDark ? 'rgba(25,135,84,0.15)' : '#f0fdf4', padding: '3px 10px', borderRadius: 6 }}>{session.domain}</span>
          <span style={{ fontSize: 11, color: c.textDim, marginLeft: 'auto' }}>
            {session.level}
          </span>
        </div>
        <h3 style={{ fontSize: 15, fontWeight: 800, color: c.title, marginBottom: 8, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {session.title}
        </h3>
        {session.instructor && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: c.textDim, marginBottom: 12 }}>
            <FaUserTie style={{ color: '#fd7e14' }} />{session.instructor}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12, marginBottom: 12 }}>
          {[
            { icon: <FaCalendarAlt />, label: 'Début', val: fmt(session.startDate) },
            { icon: <FaMapMarkerAlt />, label: 'Lieu', val: session.city },
            { icon: <FaRegClock />, label: 'Durée', val: session.duration },
            { icon: <FaUsers />, label: 'Places', val: `${session.seatsAvailable || 0}/${session.seats || 0}` },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#198754' }}>{item.icon}</span>
              <div>
                <div style={{ fontSize: 10, color: c.textDim, fontWeight: 600 }}>{item.label}</div>
                <div style={{ color: c.text, fontWeight: 700 }}>{item.val || '—'}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Barre de remplissage */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: c.textDim, marginBottom: 4 }}>
            <span>Taux de remplissage</span>
            <span style={{ color: occupancyColor, fontWeight: 700 }}>{Math.round(pct)}%</span>
          </div>
          <div style={{ height: 4, background: isDark ? 'rgba(255,255,255,0.1)' : '#f1f5f9', borderRadius: 4 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: occupancyColor, borderRadius: 4, transition: 'width 0.8s ease' }} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` }}>
          <div>
            <div style={{ fontSize: 10, color: c.textDim, fontWeight: 600, marginBottom: 2 }}>Prix</div>
            <div style={{ fontSize: 17, fontWeight: 900, color: '#198754' }}>{session.price}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => onEdit(session)} style={{
              width: 34, height: 34, borderRadius: 9, border: 'none', cursor: 'pointer',
              background: isDark ? 'rgba(253,126,20,0.15)' : '#fff7ed', color: '#fd7e14',
              fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><FaEdit /></button>
            <button onClick={() => onDelete(session)} style={{
              width: 34, height: 34, borderRadius: 9, border: 'none', cursor: 'pointer',
              background: isDark ? 'rgba(220,53,69,0.15)' : '#fff5f5', color: '#dc3545',
              fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><FaTrash /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

//  Carte CERTIFICATION ─
const CertifCard = ({ cert, onEdit, onDelete, onToggle, isDark, c }) => (
  <div style={{
    background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9'}`,
    borderLeft: `4px solid ${cert.color || '#198754'}`,
    borderRadius: 16, overflow: 'hidden', transition: 'all 0.3s ease',
    boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.06)',
    opacity: cert.is_active === false ? 0.6 : 1,
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.15)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.06)'; }}
  >
    <div style={{ position: 'relative', height: 120, overflow: 'hidden' }}>
      {cert.image ? (
        <img src={resolveImageUrl(cert.image)} alt={cert.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${cert.color || '#198754'}, #20c997)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FaCertificate style={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
        </div>
      )}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.6))' }} />
      <div style={{ position: 'absolute', top: 10, left: 10, background: `${cert.color || '#198754'}`, color: 'white', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 6 }}>
        {cert.code}
      </div>
      <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6 }}>
        <button onClick={() => onToggle(cert)} style={{
          width: 28, height: 28, borderRadius: 7, border: 'none', cursor: 'pointer',
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
          color: cert.is_active !== false ? '#20c997' : '#9ca3af', fontSize: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{cert.is_active !== false ? <FaToggleOn /> : <FaToggleOff />}</button>
      </div>
    </div>

    <div style={{ padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <FaBook style={{ color: cert.color || '#198754', fontSize: 11 }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: cert.color || '#198754' }}>{cert.domain}</span>
        <span style={{ fontSize: 11, color: c.textDim, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          <FaClock style={{ fontSize: 10 }} />{cert.duration || cert.certDuration}
        </span>
      </div>
      <h3 style={{ fontSize: 14, fontWeight: 800, color: c.title, marginBottom: 8, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {cert.title}
      </h3>
      {cert.niveau && (
        <div style={{ fontSize: 11, color: c.textDim, marginBottom: 8, padding: '4px 10px', background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', borderRadius: 6, display: 'inline-block' }}>
          {cert.niveau}
        </div>
      )}

      {cert.modules && cert.modules.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: c.textDim, textTransform: 'uppercase', marginBottom: 6 }}>Modules</div>
          {cert.modules.slice(0, 2).map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, color: c.text, marginBottom: 3 }}>
              <FaCheck style={{ color: '#20c997', fontSize: 9, marginTop: 2, flexShrink: 0 }} />
              <span style={{ lineHeight: 1.4 }}>{m}</span>
            </div>
          ))}
          {cert.modules.length > 2 && <span style={{ fontSize: 11, color: c.textDim, fontStyle: 'italic' }}>+{cert.modules.length - 2} autres modules</span>}
        </div>
      )}

      {cert.debouches && cert.debouches.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
          {cert.debouches.slice(0, 2).map((d, i) => (
            <span key={i} style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 5, background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9', color: c.text }}>{d}</span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` }}>
        <div>
          <div style={{ fontSize: 10, color: c.textDim, fontWeight: 600, marginBottom: 2 }}>Prix</div>
          <div style={{ fontSize: 16, fontWeight: 900, color: cert.color || '#198754' }}>{cert.price}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onEdit(cert)} style={{
            width: 34, height: 34, borderRadius: 9, border: 'none', cursor: 'pointer',
            background: isDark ? 'rgba(253,126,20,0.15)' : '#fff7ed', color: '#fd7e14',
            fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><FaEdit /></button>
          <button onClick={() => onDelete(cert)} style={{
            width: 34, height: 34, borderRadius: 9, border: 'none', cursor: 'pointer',
            background: isDark ? 'rgba(220,53,69,0.15)' : '#fff5f5', color: '#dc3545',
            fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><FaTrash /></button>
        </div>
      </div>
    </div>
  </div>
);

//  Page principale Formations 
const CreateFormation = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [activeTab, setActiveTab] = useState('sessions');
  const [sessions, setSessions] = useState([]);
  const [certifs, setCertifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');

  const showToast = (message, type = 'success') => setToast({ message, type });

  //  Thème 
  const c = isDark ? {
    bg: '#090c16', card: 'rgba(255,255,255,0.04)', cardB: 'rgba(255,255,255,0.08)',
    text: '#c8d4e0', textDim: '#5a6b82', title: '#eef2f7',
    surface: 'rgba(255,255,255,0.04)', shadow: '0 4px 24px rgba(0,0,0,0.4)',
    tabBg: 'rgba(255,255,255,0.06)',
  } : {
    bg: '#f0f3f8', card: '#ffffff', cardB: 'rgba(0,0,0,0.07)',
    text: '#4d6070', textDim: '#8fa3b5', title: '#1a2535',
    surface: 'rgba(0,0,0,0.04)', shadow: '0 2px 16px rgba(0,0,0,0.07)',
    tabBg: 'rgba(0,0,0,0.05)',
  };

  const mainML = `${SB_GAP + (sidebarOpen ? SB_OPEN : SB_CLOSED) + 12}px`;
  const mainPT = `${NB_TOP + NB_HEIGHT + 12}px`;
  const token = localStorage.getItem('admin_token');

  // Mapping snake_case (Laravel) vers camelCase (React)
  const mapSession = (s) => ({
    ...s,
    isPopular: s.isPopular ?? s.is_popular ?? false,
    isNewSession: s.isNewSession ?? s.is_new_session ?? false,
    seatsAvailable: s.seatsAvailable ?? s.seats_available ?? 0,
    startDate: s.startDate ?? s.start_date ?? null,
    endDate: s.endDate ?? s.end_date ?? null,
    is_active: s.is_active ?? true,
    form_type: 'session',
  });

  const mapCertif = (c) => ({
    ...c,
    is_active: c.is_active ?? true,
    form_type: 'certification',
  });

  // Fetch les deux listes en parallele avec Promise.all
  const fetchData = useCallback(() => {
    setLoading(true);

    Promise.all([
      fetch(`${API_URL}/formations/sessions/public`).then(r => r.json()),
      fetch(`${API_URL}/formations/certifications/public`).then(r => r.json()),
    ])
      .then(([sessRes, certRes]) => {
        setSessions((sessRes.data || sessRes || []).map(mapSession));
        setCertifs((certRes.data || certRes || []).map(mapCertif));
      })
      .catch(() => { setSessions([]); setCertifs([]); })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  //  Sauvegarde (create ou update) ─
  const handleSave = async (formData, id) => {
  const type = formData.get('form_type');
  const endpoint = type === 'session'
    ? `${API_URL}/formations/sessions`
    : `${API_URL}/formations/certifications`;
  const url = id ? `${endpoint}/${id}` : endpoint;

  try {
    // Nettoyage du FormData
    for (let [key, value] of formData.entries()) {
      if (value === "" || value === null || value === undefined) {
        formData.delete(key);
      }
    }

    // Method spoofing Laravel : toujours POST, mais _method=PUT pour modification
    if (id) {
      formData.append('_method', 'PUT');
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: formData,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      console.log("ERREUR BACKEND :", data);
      if (data.errors) {
        const messages = Object.values(data.errors).flat().join(", ");
        throw new Error(messages);
      }
      throw new Error(data.message || 'Erreur serveur');
    }

    showToast(
      id ? 'Formation modifiée avec succès' : 'Formation créée avec succès',
      'success'
    );

    setShowForm(false);
    setEditItem(null);
    fetchData();

  } catch (e) {
    console.error("ERREUR FRONT :", e);
    showToast(e.message || 'Une erreur est survenue', 'error');
  }
};
  //  Suppression 
  const handleDelete = async () => {
    if (!deleteTarget) return;
    const isSession = deleteTarget.form_type === 'session' || !deleteTarget.code;
    const endpoint = isSession ? `${API_URL}/formations/sessions` : `${API_URL}/formations/certifications`;

    try {
      const res = await fetch(`${endpoint}/${deleteTarget.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      showToast('Formation supprimée', 'success');
      fetchData();
    } catch {
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  //  Toggle actif/inactif 
  const handleToggle = async (item) => {
    const isSession = item.form_type === 'session' || !item.code;
    const endpoint = isSession ? `${API_URL}/formations/sessions` : `${API_URL}/formations/certifications`;

    try {
      await fetch(`${endpoint}/${item.id}/toggle`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
      showToast(`Formation ${item.is_active !== false ? 'désactivée' : 'activée'}`, 'success');
      fetchData();
    } catch {
      showToast('Erreur lors du changement de statut', 'error');
    }
  };

  //  Filtrage local ─
  const filteredSessions = sessions.filter(s =>
    !search || s.title?.toLowerCase().includes(search.toLowerCase()) || s.domain?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredCertifs = certifs.filter(c =>
    !search || c.title?.toLowerCase().includes(search.toLowerCase()) || c.domain?.toLowerCase().includes(search.toLowerCase()) || c.code?.toLowerCase().includes(search.toLowerCase())
  );

  const handleEdit = (item) => { setEditItem(item); setShowForm(true); };

  return (
    <div style={{ background: c.bg, minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
        *,*::before,*::after { box-sizing:border-box }
        @keyframes fm-shimmer  { from{background-position:200% 0} to{background-position:-200% 0} }
        @keyframes fm-fadeIn   { from{opacity:0} to{opacity:1} }
        @keyframes fm-scaleIn  { from{opacity:0;transform:scale(0.92)} to{opacity:1;transform:scale(1)} }
        @keyframes fm-slideIn  { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes fm-spin     { to{transform:rotate(360deg)} }
        @keyframes fm-fadeUp   { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        .fm-main { transition:margin-left 0.28s cubic-bezier(0.4,0,0.2,1) }
        .fm-card-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:20px; animation:fm-fadeUp 0.5s ease }
        @media(max-width:1024px){ .fm-main{margin-left:0 !important} }
      `}</style>

      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Navbar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} pageTitle="Gestion des Formations" />

      <main className="fm-main" style={{ marginLeft: mainML, paddingTop: mainPT }}>
        <div style={{ padding: '20px 24px 40px' }}>

          {/*  En-tête de page  */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14,
                  background: 'linear-gradient(135deg, #198754, #20c997)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, color: 'white', boxShadow: '0 4px 16px rgba(25,135,84,0.35)',
                }}>
                  <FaGraduationCap />
                </div>
                <div>
                  <h1 style={{ fontSize: 22, fontWeight: 900, color: c.title, margin: 0, letterSpacing: '-0.3px' }}>
                    Gestion des Formations
                  </h1>
                  <p style={{ fontSize: 13, color: c.textDim, margin: 0 }}>
                    Créez et gérez les sessions en cours et les certifications CINEF
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => { setEditItem(null); setShowForm(true); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 24px',
                background: 'linear-gradient(135deg, #198754, #20c997)',
                border: 'none', borderRadius: 12, color: 'white',
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                boxShadow: '0 4px 16px rgba(25,135,84,0.35)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <FaPlus style={{ fontSize: 13 }} />
              Nouvelle formation
            </button>
          </div>

          {/*  Stats rapides  */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
            {[
              { label: 'Sessions en cours', val: sessions.length, color: '#198754', icon: <FaCalendarAlt /> },
              { label: 'Sessions actives', val: sessions.filter(s => s.is_active !== false).length, color: '#20c997', icon: <FaToggleOn /> },
              { label: 'Certifications', val: certifs.length, color: '#fd7e14', icon: <FaCertificate /> },
              { label: 'Certifs actives', val: certifs.filter(c => c.is_active !== false).length, color: '#6f42c1', icon: <FaCheck /> },
            ].map((s, i) => (
              <div key={i} style={{
                background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9'}`,
                borderRadius: 14, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14,
                boxShadow: c.shadow,
              }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${s.color}18`, color: s.color, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {s.icon}
                </div>
                <div>
                  {loading ? <Sk h={16} r={6} mb={4} /> : <div style={{ fontSize: 26, fontWeight: 900, color: c.title, lineHeight: 1 }}>{s.val}</div>}
                  <div style={{ fontSize: 12, fontWeight: 600, color: c.textDim, marginTop: 4 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/*  Barre onglets + recherche  */}
          <div style={{
            background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9'}`,
            borderRadius: 16, padding: '16px 20px', marginBottom: 24,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
            boxShadow: c.shadow,
          }}>
            {/* Onglets */}
            <div style={{ display: 'flex', gap: 6, padding: '4px', background: isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc', borderRadius: 12 }}>
              {[
                { v: 'sessions', label: 'Sessions en cours', icon: <FaCalendarAlt />, count: filteredSessions.length },
                { v: 'certifications', label: 'Certifications CINEF', icon: <FaCertificate />, count: filteredCertifs.length },
              ].map(tab => (
                <button key={tab.v} onClick={() => setActiveTab(tab.v)} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '9px 18px', border: 'none', borderRadius: 9, cursor: 'pointer',
                  fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, fontWeight: 700,
                  background: activeTab === tab.v ? (isDark ? 'rgba(255,255,255,0.12)' : '#ffffff') : 'transparent',
                  color: activeTab === tab.v ? '#198754' : c.textDim,
                  boxShadow: activeTab === tab.v && !isDark ? '0 1px 8px rgba(0,0,0,0.09)' : 'none',
                  transition: 'all 0.2s',
                }}>
                  {tab.icon}
                  {tab.label}
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                    background: activeTab === tab.v ? 'rgba(25,135,84,0.15)' : isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
                    color: activeTab === tab.v ? '#198754' : c.textDim,
                  }}>{tab.count}</span>
                </button>
              ))}
            </div>

            {/* Recherche */}
            <div style={{ position: 'relative', flex: '0 1 280px' }}>
              <FaSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: c.textDim, fontSize: 13 }} />
              <input
                type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher une formation..."
                style={{
                  width: '100%', padding: '9px 12px 9px 34px', borderRadius: 10, fontSize: 13,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                  color: c.text, outline: 'none',
                }}
              />
            </div>
          </div>

          {/*  Grille des formations  */}
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 20 }}>
              {[1, 2, 3, 4, 5, 6].map(i => <Sk key={i} h={360} />)}
            </div>
          ) : activeTab === 'sessions' ? (
            filteredSessions.length > 0 ? (
              <div className="fm-card-grid">
                {filteredSessions.map(s => (
                  <SessionCard key={s.id} session={s} onEdit={handleEdit} onDelete={setDeleteTarget} onToggle={handleToggle} isDark={isDark} c={c} />
                ))}
              </div>
            ) : (
              <EmptyState type="sessions" onAdd={() => { setEditItem(null); setShowForm(true); }} isDark={isDark} c={c} />
            )
          ) : (
            filteredCertifs.length > 0 ? (
              <div className="fm-card-grid">
                {filteredCertifs.map(cert => (
                  <CertifCard key={cert.id} cert={cert} onEdit={handleEdit} onDelete={setDeleteTarget} onToggle={handleToggle} isDark={isDark} c={c} />
                ))}
              </div>
            ) : (
              <EmptyState type="certifications" onAdd={() => { setEditItem(null); setShowForm(true); }} isDark={isDark} c={c} />
            )
          )}
        </div>
      </main>

      {/*  Modals  */}
      {showForm && (
        <FormationForm
          onClose={() => { setShowForm(false); setEditItem(null); }}
          onSave={handleSave}
          editData={editItem}
          isDark={isDark}
          c={c}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          item={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          isDark={isDark}
          c={c}
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

//  État vide ─
const EmptyState = ({ type, onAdd, isDark, c }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '80px 32px', textAlign: 'center',
    background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
    border: `2px dashed ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
    borderRadius: 20,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  }}>
    <div style={{
      width: 80, height: 80, borderRadius: 20, marginBottom: 24,
      background: isDark ? 'rgba(25,135,84,0.1)' : '#f0fdf4',
      border: `1px solid rgba(25,135,84,0.2)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 36, color: '#198754',
    }}>
      {type === 'sessions' ? <FaCalendarAlt /> : <FaCertificate />}
    </div>
    <h3 style={{ fontSize: 20, fontWeight: 800, color: c.title, marginBottom: 10 }}>
      Aucune {type === 'sessions' ? 'session en cours' : 'certification'} pour le moment
    </h3>
    <p style={{ fontSize: 14, color: c.textDim, marginBottom: 28, maxWidth: 380, lineHeight: 1.7 }}>
      {type === 'sessions'
        ? 'Créez votre première session de formation pour qu\'elle apparaisse sur le site.'
        : 'Créez votre première certification CINEF pour qu\'elle apparaisse sur le site.'}
    </p>
    <button onClick={onAdd} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '12px 28px',
      background: 'linear-gradient(135deg, #198754, #20c997)',
      border: 'none', borderRadius: 12, color: 'white',
      fontSize: 14, fontWeight: 700, cursor: 'pointer',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      boxShadow: '0 4px 16px rgba(25,135,84,0.35)',
    }}>
      <FaPlus /> Créer une {type === 'sessions' ? 'session' : 'certification'}
    </button>
  </div>
);

export default CreateFormation;